import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux-hooks';
import { setAcraGeneratingCampaignId } from '../../../store/common.slice';
import AcraService from '../../../services/acra.service';

// Poll generation status every 15 s (generation typically takes 20 s – 2 min).
const POLL_MS = 15_000;

// Rotating status lines shown while the assessment is being generated.
const GENERATING_MESSAGES = [
  'Generating the assessment scenario for your campaign…',
  'Building the situation brief and fact sheet…',
  'Preparing the seed set and session record…',
  'Running validation checks on the generated assessment…',
];
const GENERATING_MESSAGE_INTERVAL_MS = 7_000;

type NotifState = 'generating' | 'success' | 'error';

/**
 * Floating notification tracking background assessment generation after a
 * campaign is created (pattern: AIQNotification in the consumer app). The
 * tracked campaign id lives in Redux, mirrored to localStorage so a page
 * refresh resumes tracking. Mounted once in the main layout.
 */
const AssessmentNotification: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const trackedCampaignId = useAppSelector(
    (s) => s.common.acraGeneratingCampaignId
  );

  const [notifState, setNotifState] = useState<NotifState>('generating');
  const [isMinimized, setIsMinimized] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [messageIndex, setMessageIndex] = useState(0);

  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeCampaignId = useRef<string | null>(null);

  const stopPolling = () => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  };

  // Start / restart polling whenever the tracked campaign changes.
  useEffect(() => {
    if (!trackedCampaignId) {
      stopPolling();
      activeCampaignId.current = null;
      setIsMinimized(false);
      setErrorMsg(null);
      setNotifState('generating');
      return;
    }

    if (trackedCampaignId === activeCampaignId.current) return;

    activeCampaignId.current = trackedCampaignId;
    setIsMinimized(false);
    setNotifState('generating');
    setErrorMsg(null);
    startPolling(trackedCampaignId);

    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedCampaignId]);

  // Rotate the generating status line while generating.
  useEffect(() => {
    if (notifState !== 'generating') return;
    setMessageIndex(0);
    const id = setInterval(() => {
      setMessageIndex((i) => (i + 1) % GENERATING_MESSAGES.length);
    }, GENERATING_MESSAGE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [notifState]);

  const scheduleNext = (campaignId: string) => {
    pollTimer.current = setTimeout(() => {
      if (activeCampaignId.current === campaignId) startPolling(campaignId);
    }, POLL_MS);
  };

  const startPolling = (campaignId: string) => {
    const tick = async () => {
      // Guard: tracking may have been cleared while the request was in-flight.
      if (activeCampaignId.current !== campaignId) return;

      try {
        const res = await AcraService.getAssessment(campaignId);
        if (activeCampaignId.current !== campaignId) return;
        const data = res.data.data;

        if (!data || !data.assessment) {
          // Kickoff failed server-side (or campaign vanished) — nothing to
          // keep polling for.
          setErrorMsg(
            'Assessment generation could not start. Open the campaign to start it.'
          );
          setNotifState('error');
          stopPolling();
          return;
        }

        if (data.assessment.generationStatus === 'completed') {
          setNotifState('success');
          stopPolling();
          return;
        }

        if (data.assessment.generationStatus === 'failed') {
          setErrorMsg(
            data.assessment.generationError ||
              'Assessment generation failed. Open the campaign to try again.'
          );
          setNotifState('error');
          stopPolling();
          return;
        }

        scheduleNext(campaignId);
      } catch {
        // Transient error — keep polling.
        scheduleNext(campaignId);
      }
    };
    tick();
  };

  const dismissFully = () => {
    stopPolling();
    activeCampaignId.current = null;
    dispatch(setAcraGeneratingCampaignId(null));
  };

  const openCampaign = () => {
    const campaignId = trackedCampaignId;
    dismissFully();
    if (campaignId) navigate(`/acra/${campaignId}`);
  };

  const handleCloseClick = () => {
    // Full close only once generation succeeded; otherwise minimize so the
    // admin can't silently lose a running/failed generation.
    if (notifState === 'success') {
      dismissFully();
      return;
    }
    setIsMinimized(true);
  };

  if (!trackedCampaignId) return null;

  if (isMinimized) {
    const statusText =
      notifState === 'generating'
        ? 'Assessment is generating'
        : notifState === 'success'
          ? 'Assessment is ready'
          : 'Assessment generation failed';

    return (
      <button
        type="button"
        className="fixed bottom-6 right-4 z-[9999] h-12 w-12 rounded-full border border-[#E2E8F0] bg-white shadow-2xl flex items-center justify-center hover:scale-[1.03] transition-transform"
        onClick={() => setIsMinimized(false)}
        aria-label="Open assessment notification"
        title={statusText}
      >
        <span className="text-[15px] font-semibold text-[#020817]">A</span>
        <span
          className={`absolute top-1 right-1 h-2.5 w-2.5 rounded-full ${
            notifState === 'generating'
              ? 'bg-blue-500'
              : notifState === 'success'
                ? 'bg-green-500'
                : 'bg-red-500'
          }`}
        />
      </button>
    );
  }

  return (
    <div
      className="fixed top-4 right-4 z-[9999] w-[320px] rounded-2xl shadow-2xl border border-[#E2E8F0] bg-white overflow-hidden"
      role="status"
      aria-live="polite"
    >
      {/* Header row */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#F1F5F9]">
        <span className="text-[13px] font-semibold text-[#020817]">
          Campaign Assessment
        </span>
        <button
          className="text-[#94A3B8] hover:text-[#020817] transition-colors"
          onClick={handleCloseClick}
          aria-label="Dismiss"
          type="button"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M12 4L4 12M4 4l8 8"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className="px-4 py-4">
        {notifState === 'generating' && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 animate-spin rounded-full h-5 w-5 border-b-2 border-[#020817]" />
              <p className="text-[13px] font-medium text-[#020817] leading-tight">
                Generating assessment…
              </p>
            </div>
            <p className="text-[12px] text-[#64748B] transition-opacity duration-300">
              {GENERATING_MESSAGES[messageIndex]}
            </p>
            <p className="text-[11px] text-[#94A3B8]">
              This usually takes 1–2 minutes. You can continue using the
              dashboard.
            </p>
          </div>
        )}

        {notifState === 'success' && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path
                    d="M2 7l3.5 3.5L11 3"
                    stroke="#22c55e"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <p className="text-[13px] font-medium text-[#020817]">
                Assessment is ready!
              </p>
            </div>
            <p className="text-[12px] text-[#64748B]">
              Candidates can now be invited to this campaign.
            </p>
            <button
              type="button"
              className="w-full bg-[#020817] text-white py-2.5 px-4 rounded-full text-[13px] font-medium cursor-pointer hover:opacity-90 transition-opacity"
              onClick={openCampaign}
            >
              Add candidates →
            </button>
          </div>
        )}

        {notifState === 'error' && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                  <path
                    d="M9 2L2 9M2 2l7 7"
                    stroke="#ef4444"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <p className="text-[13px] font-medium text-[#020817]">
                Assessment generation failed
              </p>
            </div>
            {errorMsg && (
              <p className="text-[12px] text-[#64748B] leading-relaxed">
                {errorMsg}
              </p>
            )}
            <button
              type="button"
              className="w-full border border-[#E2E8F0] text-[#020817] py-2 px-4 rounded-full text-[13px] font-medium cursor-pointer hover:bg-[#F8FAFC] transition-colors"
              onClick={openCampaign}
            >
              Open campaign
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentNotification;
